import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Hook for managing modal state with a standardized API
 * 
 * Usage:
 * const { isOpen, open, close, title, content, openWith } = useModal();
 * 
 * // Simple open/close
 * <button onClick={open}>Open</button>
 * <Modal isOpen={isOpen} onClose={close} title={title}>{content}</Modal>
 * 
 * // Open with specific content
 * openWith({ title: 'My Title', content: <MyContent /> });
 */
const useModal = (initialState = false) => {
  const [isOpen, setIsOpen] = useState(initialState);
  const [modalData, setModalData] = useState({ title: '', content: null });
  const clearModalDataTimeoutRef = useRef(null);

  const clearPendingModalDataReset = useCallback(() => {
    if (!clearModalDataTimeoutRef.current) return;
    clearTimeout(clearModalDataTimeoutRef.current);
    clearModalDataTimeoutRef.current = null;
  }, []);

  const open = useCallback(() => {
    clearPendingModalDataReset();
    setIsOpen(true);
  }, [clearPendingModalDataReset]);

  const close = useCallback(() => {
    setIsOpen(false);
    clearPendingModalDataReset();
    // Clear content after a short delay to allow close animation
    clearModalDataTimeoutRef.current = setTimeout(() => {
      setModalData({ title: '', content: null });
      clearModalDataTimeoutRef.current = null;
    }, 300);
  }, [clearPendingModalDataReset]);

  const toggle = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  /**
   * Open modal with specific content
   * @param {Object} data - Modal data
   * @param {string} data.title - Modal title
   * @param {React.ReactNode} data.content - Modal content
   */
  const openWith = useCallback(({ title = '', content = null }) => {
    clearPendingModalDataReset();
    setModalData({ title, content });
    setIsOpen(true);
  }, [clearPendingModalDataReset]);

  useEffect(() => () => {
    clearPendingModalDataReset();
  }, [clearPendingModalDataReset]);

  return {
    isOpen,
    open,
    close,
    toggle,
    openWith,
    title: modalData.title,
    content: modalData.content,
  };
};

export default useModal;

